<template>
  <a-modal
    v-model="localVisible"
    :title="title || $t('指定库存批次')"
    :width="width"
    :confirmLoading="saving"
    :footer="null"
    @cancel="onCancel"
  >
    <a-table
      :columns="columns"
      :dataSource="list"
      :loading="loading"
      :pagination="false"
      size="middle"
      bordered
      rowKey="inventoryItemId"
      :rowSelection="{
        type: 'radio',
        selectedRowKeys: selectedInventoryItemId ? [selectedInventoryItemId] : [],
        onChange: onSelectChange,
        getCheckboxProps: (record) => ({
          props: {
            disabled: !isBatchCodeSelectable(record),
          },
        }),
      }"
    />

    <div style="text-align: right; margin-top: 16px">
      <a-button style="margin-right: 8px" @click="onCancel">
        {{ cancelText || $t('form.Cancel') }}
      </a-button>
      <a-button type="primary" :loading="saving" @click="onOk">
        {{ okText || $t('form.Save') }}
      </a-button>
    </div>
  </a-modal>
</template>

<script>
import http from '@/service/axios'
import api from '@/service/api'
import { toThousands } from '@/common/filters'

export default {
  name: 'InventoryBatchSelectModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    width: {
      type: Number,
      default: 1000,
    },
    okText: {
      type: String,
      default: '',
    },
    cancelText: {
      type: String,
      default: '',
    },
    barcode: {
      type: String,
      default: '',
    },
    warehouseCode: {
      type: String,
      default: '',
    },
    pageNo: {
      type: Number,
      default: 1,
    },
    pageSize: {
      type: Number,
      default: 50,
    },
    // update params
    updateUrl: {
      type: String,
      required: true,
    },
    orderId: {},
    orderItemId: {},
    orderIdParamName: {
      default: 'transferOrderId',
    },
    orderItemIdParamName: {
      default: 'transferOrderItemId',
    },
  },
  data() {
    return {
      localVisible: this.visible,
      loading: false,
      list: [],
      selectedInventoryItemId: null,
      selectedBatchCode: '',
      selectedInventoryNote: '',
      selectedUnitPrice: '',
      saving: false,
    }
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t('批次编码'),
          dataIndex: 'batchCode',
          width: 180,
        },
        {
          title: this.$t('可用库存'),
          dataIndex: 'availableQuantity',
          width: 120,
          customRender: (text) => (text !== undefined && text !== null ? toThousands(text) : '-'),
        },
        {
          title: this.$t('库存来源'),
          dataIndex: 'note',
          width: 260,
          customRender: (text) => {
            const obj = this.parseNote(text)
            if (!obj) return text || '-'
            const h = this.$createElement
            const children = []
            if (obj.source) children.push(h('div', `${this.$t('来源')}: ${obj.source}`))
            if (obj.imei) children.push(h('div', `IMEI: ${this.formatImei(obj.imei)}`))
            if (obj.battery) children.push(h('div', `${this.$t('电池')}: ${obj.battery}`))
            if (obj.appearance) children.push(h('div', `${this.$t('外观')}: ${obj.appearance}`))
            if (obj.note) children.push(h('div', `${this.$t('备注')}: ${obj.note}`))
            return h('div', { style: { textAlign: 'left' } }, children)
          },
        },
        {
          title: this.$t('单价'),
          dataIndex: 'unitPrice',
          width: 120,
          customRender: (text) => text || '-',
        },
        {
          title: this.$t('创建时间'),
          dataIndex: 'createdAt',
          width: 180,
          customRender: (text) => text || '-',
        },
      ]
    },
  },
  watch: {
    visible(val) {
      this.localVisible = val
      if (val) this.fetchList()
    },
    localVisible(val) {
      this.$emit('update:visible', val)
    },
  },
  methods: {
    onSelectChange(selectedRowKeys) {
      this.selectedInventoryItemId = selectedRowKeys?.[0] || null
      const item = this.list.find((v) => v.inventoryItemId === this.selectedInventoryItemId)
      this.selectedBatchCode = item ? item.batchCode : ''
      this.selectedInventoryNote = item?.note || ''
      this.selectedUnitPrice = item?.unitPrice || ''
    },
    isBatchCodeSelectable(record) {
      const batchCode = record?.batchCode
      return !!batchCode && Number(record?.availableQuantity || 0) > 0
    },
    parseNote(text) {
      if (!text) return null
      if (typeof text !== 'string') return null
      if (text.startsWith('{') && text.endsWith('}')) {
        try {
          const obj = JSON.parse(text)
          if (obj && typeof obj === 'object') {
            return obj
          }
        } catch {
          return null
        }
      }
      return null
    },
    formatImei(imei) {
      if (!imei) return ''
      if (typeof imei === 'string' && imei.startsWith('[') && imei.endsWith(']')) {
        try {
          const arr = JSON.parse(imei)
          if (Array.isArray(arr)) return arr.join(',')
        } catch (e) {
          console.log(e)
        }
      }
      return imei
    },
    async fetchList() {
      if (!this.barcode || !this.warehouseCode) {
        this.list = []
        this.selectedInventoryItemId = null
        return
      }
      this.loading = true
      this.selectedInventoryItemId = null
      this.selectedBatchCode = ''
      this.selectedInventoryNote = ''
      this.selectedUnitPrice = ''
      try {
        const res = await http({
          instance: this,
          url: api.queryInventoryItemList,
          type: 'post',
          data: {
            barcode: this.barcode,
            warehouseCode: this.warehouseCode,
            pageNo: this.pageNo,
            pageSize: this.pageSize,
          },
        })
        this.list = (res && res.result && res.result.inventoryItemList) || []
      } finally {
        this.loading = false
      }
    },
    onCancel() {
      this.localVisible = false
    },
    async onOk() {
      if (!this.selectedInventoryItemId) {
        this.$warning({
          title: this.$t('layer.Warning'),
          content: this.$t('请选择一个库存批次'),
        })
        return
      }
      if (!this.selectedBatchCode) {
        this.$warning({
          title: this.$t('layer.Warning'),
          content: this.$t('批次编码为空，不能保存'),
        })
        return
      }
      this.saving = true
      try {
        await http({
          instance: this,
          url: this.updateUrl,
          type: 'post',
          data: {
            [this.orderIdParamName]: this.orderId,
            [this.orderItemIdParamName]: this.orderItemId,
            inventoryBatchCode: this.selectedBatchCode,
            inventoryNote: this.selectedInventoryNote,
            unitPrice: this.selectedUnitPrice,
          },
        })
        this.$success({
          title: this.$t('layer.Success'),
          content: this.$t('库存批次指定成功'),
        })
        this.localVisible = false
        this.$emit('saved')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
